import { MilestoneMap1, Objective1 } from '../../../types';
import { DependencyRecord } from '../../../map_dependencies/utils/dependencyUtils';
import { DataNode } from '../types';
import { getIntervalsForMilestoneMap } from '../../../utils/getIntervalsForMilestoneMap';
import { filterItemsForInterval, getLatestStatusInInterval } from '../../../utils/statusUtils';
import { buildDependencyDataNodes } from '../utils/dependencyBuilder';

/**
 * Build Dates data node structure
 *
 * Structure: Milestone Map → Reporting Date → Objective → Status Report
 * This extracts and reuses the logic from the existing createTreeViewByDates system.
 */
export function buildDatesDataNode(
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
  dependencyRecords: DependencyRecord[] = [],
  allMaps: MilestoneMap1[] = [],
): DataNode<MilestoneMap1> {
  // Create the root milestone map node
  const rootNode: DataNode<MilestoneMap1> = {
    id: milestoneMap.uuid,
    data: milestoneMap,
    children: [],
    type: 'MilestoneMap',
    metadata: {
      defaultExpanded: true,
    },
  };

  // Build the date hierarchy
  const dateItems = createDateDataNodes(milestoneMap, objectives, dependencyRecords, allMaps);

  rootNode.children = dateItems;

  return rootNode;
}

/**
 * Create date data nodes with objectives as children
 *
 * This extracts the logic from createTreeViewByDates in the existing system
 */
function createDateDataNodes(
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
  dependencyRecords: DependencyRecord[] = [],
  allMaps: MilestoneMap1[] = [],
): DataNode<any>[] {
  const intervals = getIntervalsForMilestoneMap(milestoneMap);

  return intervals.map((interval) => {
    const objectiveStatusItems = createObjectiveStatusDataNodes(
      objectives,
      interval,
      dependencyRecords,
      allMaps,
    );

    const dateLabel = interval.end.toLocaleDateString();

    return {
      id: `date-${interval.end.toISOString()}`,
      data: {
        interval,
        dateLabel,
      },
      children: objectiveStatusItems,
      type: 'StatusReportDate',
      metadata: {
        forIntervalEnding: interval.end.toISOString().split('T')[0],
      },
    };
  });
}

/**
 * Create objective status data nodes for a specific interval
 *
 * Each objective gets status, results, and comments nodes for this particular date
 */
function createObjectiveStatusDataNodes(
  objectives: Objective1[],
  interval: { start: Date; end: Date },
  dependencyRecords: DependencyRecord[] = [],
  allMaps: MilestoneMap1[] = [],
): DataNode<any>[] {
  return objectives.flatMap((objective) => {
    const level4Nodes: DataNode<any>[] = [];

    // Find status for this interval using timezone-agnostic interval matching
    const status = getLatestStatusInInterval(objective.statuses, interval);

    // Status node - only create if there's actually a status
    if (status && status.statusCode) {
      const statusLabel = `${objective.text} - ${status.statusCode}`;
      level4Nodes.push({
        id: `${interval.end.toISOString()}-${objective.uuid}-status`,
        data: {
          objective,
          status,
          statusLabel,
          interval,
        },
        children: [],
        type: 'Status',
        metadata: {
          statusUuid: status?.uuid,
          objectiveUuid: objective.uuid,
          forIntervalEnding: interval.end.toISOString().split('T')[0],
        },
      });
    }

    // Results nodes - add all results due in this interval
    const resultsForInterval = filterItemsForInterval(
      objective.results || [],
      interval.start,
      interval.end,
    );

    resultsForInterval.forEach((result) => {
      // Build dependency children for this result
      const dependencyChildren = buildDependencyDataNodes(
        result,
        dependencyRecords,
        allMaps,
        objectives,
      );

      level4Nodes.push({
        id: `${interval.end.toISOString()}-${objective.uuid}-${result.uuid}`,
        data: {
          result,
          objective,
          interval,
        },
        children: dependencyChildren,
        type: result.isKeyResult ? 'KeyResult' : 'Result',
        metadata: {
          objectiveUuid: objective.uuid,
          forIntervalEnding: interval.end.toISOString().split('T')[0],
        },
      });
    });

    // TODO: Add Comments nodes when needed
    // Comments would follow the same pattern as Results

    return level4Nodes;
  });
}
